import { Fragment, h } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'

import {
  FindStepCallback,
  buildStepFinder,
  findFirstEnabled,
  findFirstIndex,
} from '~utils/steps'
import { useDispatch } from 'react-redux'
import { Dispatch } from 'redux'

import type { FlowVariants } from '~types/commons'
import type { CaptureKeys, GlobalActions } from '~types/redux'
import type {
  ChangeFlowProp,
  ComponentStep,
  HistoryLocationState,
  HistoryRouterProps,
  HistoryRouterWrapperProps,
} from '~types/routers'
import type { SdkResponse } from '~types/sdk'
import type {
  DocumentTypes,
  StepConfig,
  StepOptionDocument,
} from '~types/steps'
import usePassiveSignals from '~contexts/usePassiveSignals'
import { setIdDocumentType } from '../ReduxAppWrapper/store/actions/globals'
import { sendEvent } from '../../Tracker'

import { useHistory } from './useHistory'
import { buildComponentsList } from './StepComponentMap'
import StepsRouter from './StepsRouter'
import useMotion from '~contexts/useMotion'
import captureScreenPerformance from '~core/PerformanceAnalytics/helpers/captureScreenPerformance'
import useSdkConfigurationService from '~core/SdkConfiguration/useSdkConfigurationService'
import type { SdkConfiguration } from '~core/SdkConfiguration/types'
import { getCountryDataForDocumentType } from '~supported-documents'
import withTheme from 'components/Theme'
import GenericError from 'components/GenericError'
import { getUnsupportedMobileBrowserError, isDesktop } from '~utils'

type HistoryRouterState = {
  initialStep: number
} & HistoryLocationState

export const HistoryRouterWrapper = (props: HistoryRouterWrapperProps) => {
  const { useSteps, fallback, token, triggerOnError } = props
  const { steps, error, loading, parsedError, ...useStepsProps } = useSteps()

  // TODO: this hook returns an instance of PassiveSignalsTracker,
  // use it to customisably track behaviours like screen switching.
  usePassiveSignals({ token })

  if (!steps || loading) {
    return <Fragment>{fallback}</Fragment>
  }

  if (error) {
    if (parsedError?.response?.error?.message) {
      triggerOnError({
        status: parsedError.status,
        response: parsedError.response.error,
      })
    }
    return (
      <div data-page-id={'Error'}>
        <p>There was a server error!</p>
        <p data-qa="error">{error}</p>
        <p>Please try reloading the app, and try again.</p>
      </div>
    )
  }

  return <HistoryRouter {...props} {...useStepsProps} steps={steps} />
}

// Wrap components with theme that include navigation and footer
const WrappedError = withTheme(GenericError)

const requiresCameraOnMobile = (
  findStep: FindStepCallback,
  sdkConfiguration: SdkConfiguration
): boolean => {
  if (
    sdkConfiguration.document_capture?.enable_native_camera_fallback !== true
  ) {
    return true
  }

  const face = findStep('face')

  if (
    sdkConfiguration.face_selfie_capture?.enable_native_camera_fallback !== true
  ) {
    return true
  }

  if (
    face?.options?.requestedVariant === 'video' ||
    face?.options?.requestedVariant === 'motion'
  ) {
    return true
  }

  return false
}

const requiresVideoRecording = (findStep: FindStepCallback): boolean => {
  const faceStep = findStep('face')

  const photoCaptureFallback = faceStep?.options?.photoCaptureFallback ?? true

  return (
    !photoCaptureFallback &&
    (faceStep?.options?.requestedVariant === 'video' ||
      faceStep?.options?.requestedVariant === 'motion')
  )
}

const checkUnsupportedBrowserError = (
  findStep: FindStepCallback,
  sdkConfiguration: SdkConfiguration,
  hasCamera: boolean | null | undefined
): boolean => {
  if (isDesktop) {
    return false
  }

  if (
    hasCamera === false &&
    requiresCameraOnMobile(findStep, sdkConfiguration)
  ) {
    return true
  }

  if (!window.MediaRecorder && requiresVideoRecording(findStep)) {
    return true
  }

  return false
}

export const HistoryRouter = (props: HistoryRouterProps) => {
  const {
    currentStepType,
    actions,
    isNavigationDisabled,
    onFlowChange,
    captures,
    options,
    documentType,
    genericDocumentType,
    step: defaultStep,
    stepIndexType,
    deviceHasCameraSupport,
    options: { mobileFlow, useMemoryHistory },
    steps,
    hasNextStep,
    hasPreviousStep,
    loadNextStep,
    completeStep,
    token,
    triggerOnError,
    hasCamera,
  } = props

  const sdkConfiguration = useSdkConfigurationService()
  const { deviceCanUseMotion } = useMotion()
  const findStep = buildStepFinder(steps)
  const dispatch = useDispatch<Dispatch<GlobalActions>>()

  const multipleDocumentTask = (
    documentOptions: StepOptionDocument | undefined
  ): void => {
    if (documentOptions?.documentSelection?.length === 1) {
      //@ts-ignore
      const newDocumentType: DocumentTypes =
        documentOptions?.documentSelection[0].document_type
      if (newDocumentType && newDocumentType !== documentType) {
        dispatch(setIdDocumentType(newDocumentType))
      }
    }
  }

  const getComponentsList = (
    stepsConfig: StepConfig[],
    flow?: FlowVariants
  ): ComponentStep[] => {
    if (!stepsConfig) {
      throw new Error('steps not provided')
    }

    // Fix: fix for a an workflow edge case where there is two single document type task follow each other SDK-234
    if (stepsConfig.some((e) => e.type === 'document')) {
      //@ts-ignore
      const config: StepConfigDocument = stepsConfig.find(function (ele) {
        return ele.type === 'document' && ele.options !== undefined
      })
      multipleDocumentTask(config?.options)
    }

    return buildComponentsList({
      flow: flow || state.flow,
      documentType,
      genericDocumentType,
      steps: stepsConfig,
      mobileFlow,
      deviceHasCameraSupport,
      deviceCanUseMotion,
      hasPreviousStep,
      sdkConfiguration,
    })
  }

  const { back, forward, push } = useHistory(({ state: historyState }) => {
    const { step } = getComponentsList(steps, historyState.flow)[
      historyState.step
    ]

    if (step.skip) {
      historyState.step < state.step ? back() : forward()
    } else {
      setState({ ...state, ...historyState })
    }
  }, useMemoryHistory)

  const [state, setState] = useState<HistoryRouterState>(() => {
    const componentsList = getComponentsList(steps, 'captureSteps')

    const stepIndex =
      stepIndexType === 'client'
        ? findFirstIndex(componentsList, defaultStep || 0)
        : findFirstEnabled(componentsList)

    push({
      flow: 'captureSteps',
      step: stepIndex,
    })

    const stepType = componentsList[stepIndex].step.type
    actions.setCurrentStepType(stepType)

    return {
      flow: 'captureSteps',
      step: stepIndex,
      initialStep: stepIndex,
    }
  })

  const setStepIndex = (
    newStepIndex: number,
    newFlow?: FlowVariants,
    excludeStepFromHistory?: boolean
  ): void => {
    const { flow: currentFlow } = state
    const newState = {
      step: newStepIndex,
      flow: newFlow || currentFlow,
    }
    if (excludeStepFromHistory) {
      setState({ ...state, ...newState })
    } else {
      push(newState)
    }
  }

  const disableNavigation = (): boolean => {
    const getInitialStep = () => {
      return state.initialStep === state.step && state.flow === 'captureSteps'
    }

    const getStepType = (step: number) => {
      const componentList = getComponentsList(steps)
      return componentList[step] ? componentList[step].step.type : undefined
    }

    const firstEnabledStep = () =>
      state.step > 0 &&
      getComponentsList(steps)
        .slice(0, state.step)
        .every((c) => c.step.skip)

    return (
      isNavigationDisabled ||
      getInitialStep() ||
      getStepType(state.step) === 'complete' ||
      firstEnabledStep()
    )
  }

  const changeFlowTo: ChangeFlowProp = (
    newFlow,
    newStep = 0,
    excludeStepFromHistory = false
  ) => {
    const { flow: previousFlow, step: previousStepIndex } = state

    if (!onFlowChange || previousFlow === newFlow) {
      return
    }

    const previousStep = getComponentsList(steps)[previousStepIndex]
    captureScreenPerformance.onScreenChangeStart()

    onFlowChange(newFlow, {
      clientStepIndex: previousStep.stepIndex,
      clientSteps: steps,
    })

    setStepIndex(newStep, newFlow, excludeStepFromHistory)
  }

  const nextStep = () => {
    const { step: currentStep } = state
    const componentsList = getComponentsList(steps)

    const nextStepComponent = componentsList
      .slice(currentStep + 1)
      .find((c) => !c.step.skip)

    captureScreenPerformance.onScreenChangeStart()

    if (nextStepComponent) {
      const newStepIndex = componentsList.indexOf(nextStepComponent)

      setStepIndex(newStepIndex)

      const newStepType = nextStepComponent.step.type
      const isNewStepType = currentStepType !== newStepType
      if (isNewStepType) {
        actions.setCurrentStepType(newStepType)
      }
    } else if (hasNextStep) {
      loadNextStep(() => {
        setStepIndex(0, 'captureSteps')
      }, state.flow)
    }
  }

  const triggerOnComplete = useCallback(() => {
    const expectedCaptureKeys: CaptureKeys[] = [
      'document_front',
      'document_back',
      'face',
      'data',
      'poa',
    ]
    const data: SdkResponse = Object.entries(captures)
      .filter(([key, value]) => key !== 'takesHistory' && value != null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value?.metadata }), {})

    const keysWithMissingData: Array<string> = []

    expectedCaptureKeys.forEach((key) => {
      if (key in data && data[key] === undefined) {
        keysWithMissingData.push(key)
      }
    })

    if (keysWithMissingData.length) {
      triggerOnError({
        response: {
          type: 'exception',
          message: `The following keys have missing data: ${keysWithMissingData}`,
        },
      })
      return
    }

    options.events?.emit('complete', data)
  }, [captures, options.events, triggerOnError])

  const previousStep = () => {
    const { step: currentStep } = state
    setStepIndex(currentStep - 1)
  }

  const goBack = () => {
    sendEvent('navigation_back_button_clicked')
    captureScreenPerformance.onScreenChangeStart()
    back()
  }

  const getDocumentType = () => {
    const documentStep = findStep('document')

    const documentSelection = documentStep?.options?.documentSelection || []
    if (documentSelection.length === 1 && !documentType) {
      const { document_type, issuing_country } = documentSelection[0]

      const supportedCountry = getCountryDataForDocumentType(
        issuing_country,
        document_type as DocumentTypes
      )

      actions.setIdDocumentType(document_type)
      actions.setIdDocumentIssuingCountry(supportedCountry)
      return document_type as DocumentTypes
    }

    return documentType
  }

  useEffect(() => {
    if (!hasNextStep) {
      triggerOnComplete()
    }
  }, [hasNextStep, triggerOnComplete])

  if (checkUnsupportedBrowserError(findStep, sdkConfiguration, hasCamera)) {
    return (
      <WrappedError
        disableNavigation
        error={{ name: getUnsupportedMobileBrowserError() }}
      />
    )
  }

  return (
    <StepsRouter
      {...props}
      completeStep={completeStep}
      back={goBack}
      changeFlowTo={changeFlowTo}
      componentsList={getComponentsList(steps)}
      disableNavigation={disableNavigation()}
      documentType={getDocumentType()}
      nextStep={nextStep}
      previousStep={previousStep}
      step={state.step}
      triggerOnError={triggerOnError}
      isLoadingStep={status === 'loading'}
    />
  )
}
